/**
 * use-request-type-page-state.ts
 *
 * Hook condiviso per le pagine tipologia (Bozzetti, Campionatura, Produzione, Gamma).
 * Sincronizza i filtri (search, year, status, date range) con i search params dell'URL
 * via TanStack Router, applica debounce alla ricerca e gestisce paginazione.
 * Le pagine consumatrici si riducono a markup + collegamento al risultato.
 */

import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearch } from '@tanstack/react-router';
import { useDebouncedValue } from '@mantine/hooks';
import { RequestStatus, RequestType } from '../types/api';
import { useRequestsByType } from './use-requests';
import { useRequestAggregates } from './use-request-aggregates';
import type { RequestTypeFiltersValue } from '../components/requests/filters';

type RoutePath = '/bozzetti' | '/campioni' | '/produzione' | '/gamma' | '/pre-campionatura';

interface RouteSearch {
  q?: string;
  year?: number;
  status?: number;
  startDate?: string;
  endDate?: string;
  pageNumber?: number;
  pageSize?: number;
}

interface UseRequestTypePageStateOptions {
  type: RequestType;
  /**
   * Path della rotta corrente. Usato solo come hint informativo;
   * `useSearch` e `useNavigate` sono chiamati con `strict: false` per evitare
   * l'invariant di TanStack Router quando la match exact può fallire.
   */
  route: RoutePath;
  /**
   * Stati mostrati di default quando nessun chip di stato è selezionato (es. la pagina
   * Pre-Campionatura mostra solo PreCampionatura/PreCampionaturaCompleted). Se omesso,
   * la pagina mostra tutte le richieste del tipo, come le pagine tipologia esistenti.
   */
  defaultStatuses?: RequestStatus[];
}

export function useRequestTypePageState({ type, route, defaultStatuses }: UseRequestTypePageStateOptions) {
  const navigate = useNavigate();
  const location = useLocation();
  // `route` è la rotta nominale; in fallback usiamo il pathname runtime per garantire
  // che il navigate aggiorni i search params della rotta in cui il componente è realmente montato.
  const currentPath = location.pathname || route;
  const search = useSearch({ strict: false }) as RouteSearch;

  // Search testuale: stato locale per ridurre i navigate, debounced prima di chiamare l'API
  const [searchInput, setSearchInput] = useState(search.q ?? '');
  const [debouncedSearch] = useDebouncedValue(searchInput, 300);

  // Quando l'URL cambia da fuori (drill-down), sincronizza l'input
  useEffect(() => {
    setSearchInput(search.q ?? '');
  }, [search.q]);

  const pageNumber = search.pageNumber ?? 1;
  const pageSize = search.pageSize ?? 20;
  const status = (search.status as RequestStatus | undefined) ?? undefined;
  const year = search.year;
  const startDate = search.startDate;
  const endDate = search.endDate;

  const updateSearch = useCallback(
    (next: Partial<RouteSearch>) => {
      navigate({
        to: currentPath,
        search: (prev: any) => ({ ...(prev ?? {}), ...next }),
        replace: true,
      } as any);
    },
    [navigate, currentPath]
  );

  // Quando il debounce produce un valore diverso da quello in URL, aggiorna l'URL
  useEffect(() => {
    const current = search.q ?? '';
    const next = debouncedSearch || undefined;
    if ((next ?? '') !== current) {
      updateSearch({ q: next, pageNumber: 1 });
    }
  }, [debouncedSearch, search.q, updateSearch]);

  const filtersValue: RequestTypeFiltersValue = {
    searchTerm: searchInput,
    progressiveYear: year,
    status,
    startDate,
    endDate,
  };

  const handleFiltersChange = useCallback(
    (next: RequestTypeFiltersValue) => {
      // searchInput è gestito separatamente per il debounce; gli altri filtri vanno subito in URL
      if (next.searchTerm !== searchInput) {
        setSearchInput(next.searchTerm ?? '');
      }
      updateSearch({
        year: next.progressiveYear,
        status: next.status,
        startDate: next.startDate,
        endDate: next.endDate,
        pageNumber: 1,
      });
    },
    [searchInput, updateSearch]
  );

  const handleResetFilters = useCallback(() => {
    setSearchInput('');
    navigate({ to: currentPath, search: {} as any, replace: true } as any);
  }, [navigate, currentPath]);

  const handleStatusToggle = useCallback(
    (next?: RequestStatus) => {
      updateSearch({ status: next, pageNumber: 1 });
    },
    [updateSearch]
  );

  const handlePageChange = useCallback(
    (page: number) => updateSearch({ pageNumber: page }),
    [updateSearch]
  );

  const handlePageSizeChange = useCallback(
    (size: number) => updateSearch({ pageSize: size, pageNumber: 1 }),
    [updateSearch]
  );

  // Query principale: lista paginata.
  // Se nessuno stato è selezionato esplicitamente (chip/filtro) e la pagina definisce degli
  // stati di default (es. Pre-Campionatura), li usiamo come filtro implicito lato server.
  const list = useRequestsByType(type, {
    pageNumber,
    pageSize,
    searchTerm: debouncedSearch || undefined,
    progressiveYear: year,
    status,
    startDate,
    endDate,
    statuses: !status && defaultStatuses ? defaultStatuses : undefined,
  });

  // Aggregati per i contatori per status (filtrati per anno per coerenza con la pagina)
  const aggregates = useRequestAggregates({ type, year });

  return {
    // Filtri
    filters: filtersValue,
    handleFiltersChange,
    handleResetFilters,
    handleStatusToggle,
    activeStatus: status,
    // Lista
    items: list.items,
    totalCount: list.totalCount,
    totalPages: list.totalPages,
    pageNumber,
    pageSize,
    isLoading: list.isLoading,
    isFetching: list.isFetching,
    invalidate: list.invalidate,
    handlePageChange,
    handlePageSizeChange,
    // Aggregati
    aggregates: aggregates.data,
    aggregatesLoading: aggregates.isLoading,
    availableYears: aggregates.data?.availableYears ?? [],
  };
}
